// Variations in a claim: check 1 = what PBS lists on the claim, check 2 = the PM C3 register (building + amount).
// Close amounts are suggested for a PM to confirm; anything not approved in PM C3 stays listed, flagged and excluded.
import { useState } from 'react';
import { write } from '../../lib/api';
import { money } from '../../lib/claimCalc';
import { APPROVED, type RegVar, type VarCheck } from '../../lib/claimData';
import { statusLabel } from '../../lib/registers';
import { Button, errText, useToast } from '../kit';

export function Variations({
  itemId,
  checks,
  reg,
  approvedTotal,
  before,
  thisSwi,
  claimed,
  canEdit,
  onChanged,
}: {
  itemId: string;
  checks: VarCheck[];
  reg: RegVar[];
  approvedTotal: number;
  before: number;
  thisSwi: number;
  claimed: number;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ ref: '', desc: '', listed: '', now: '' });
  const [pickFor, setPickFor] = useState<string | null>(null);
  const pctOf = (v: number) => (approvedTotal ? (v / approvedTotal) * 100 : 0);
  const excluded = claimed - thisSwi;

  async function upd(c: VarCheck, values: Record<string, unknown>, summary: string) {
    try {
      await write('claim_variations', 'update', { id: c.row.id, values, summary });
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }
  async function add() {
    try {
      await write('claim_variations', 'insert', {
        values: { item_id: itemId, sort: checks.length, ref_on_claim: f.ref || null, description: f.desc || null, listed_amount: f.listed ? Number(f.listed) : null, claimed_now: f.now ? Number(f.now) : null, source: 'manual' },
        summary: `Added variation ${f.ref} to the claim`,
      });
      setF({ ref: '', desc: '', listed: '', now: '' });
      setAdding(false);
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }

  return (
    <div>
      <div className="var-sum">
        <div className="var-top">
          <span>Approved in PM C3 register</span>
          <strong>FJD {money(approvedTotal)}</strong>
        </div>
        {approvedTotal > 0 ? (
          <div className="track" role="img" aria-label={`Approved variations: ${money(pctOf(before))} % claimed before, ${money(pctOf(thisSwi))} % this claim`}>
            <i style={{ width: `${Math.min(100, pctOf(before))}%`, background: '#7a5200' }} />
            <i style={{ width: `${Math.min(100, pctOf(thisSwi))}%`, background: 'var(--orange)' }} />
          </div>
        ) : null}
        <div className="var-legend">
          <span>
            <i style={{ background: '#7a5200' }} />
            Before {money(before)} · {money(pctOf(before))} %
          </span>
          <span>
            <i style={{ background: 'var(--orange)' }} />
            <strong>
              This claim (verified) {money(thisSwi)} · {money(pctOf(thisSwi))} %
            </strong>
          </span>
          <span>
            <i style={{ background: 'var(--line-2)' }} />
            Left {money(approvedTotal - before - thisSwi)}
          </span>
        </div>
        <div className="chips">
          <span className="fchip">
            PBS claims <strong>&nbsp;{money(claimed)}&nbsp;</strong> on {checks.length} variation{checks.length === 1 ? '' : 's'}
          </span>
          <span className="fchip good">Verified {money(thisSwi)}</span>
          {excluded > 0.005 ? <span className="fchip bad">Not counted {money(excluded)}</span> : null}
        </div>
      </div>
      {checks.length ? (
        <div className="table-wrap">
          <table className="t vars">
            <thead>
              <tr>
                <th>On PBS claim (check 1)</th>
                <th>PM C3 register (check 2)</th>
                <th className="right">This claim</th>
              </tr>
            </thead>
            <tbody>
              {checks.map((c) => (
                <tr key={c.row.id} className={c.state === 'not_approved' || c.state === 'none' ? 'vbad' : c.state === 'close' ? 'vwarn' : ''}>
                  <td>
                    <span className="vchk ok" aria-hidden="true">✓</span>
                    <strong>{c.row.ref_on_claim ?? '—'}</strong> {c.row.description ?? ''}
                    <div className="vsub">Listed as approved · {money(c.row.listed_amount)}</div>
                    {c.row.source === 'ai' ? <div className="vsub">Read by SWI AI</div> : null}
                  </td>
                  <td>
                    {c.match ? (
                      <>
                        <span className={`vchk ${c.state === 'ok' ? 'ok' : c.state === 'close' ? 'amb' : 'bad'}`} aria-hidden="true">
                          {c.state === 'ok' ? '✓' : c.state === 'close' ? '≈' : '!'}
                        </span>
                        <span className="vno">{c.match.number ?? c.match.ref_no}</span> {APPROVED.has(c.match.status) ? 'Approved' : <span className="rstat bad">Not approved</span>} · {money(c.match.amount)}
                        <div className={`vsub ${c.state === 'ok' ? '' : c.state === 'close' ? 'amb-t' : 'bad-t'}`}>
                          {c.state === 'ok'
                            ? 'Matches'
                            : c.state === 'close'
                              ? c.row.match_confirmed
                                ? `Confirmed by SWI — PBS figure ${money(Math.abs(c.diff))} ${c.diff > 0 ? 'higher' : 'lower'}`
                                : c.diff
                                  ? `Close match — PBS figure ${money(Math.abs(c.diff))} ${c.diff > 0 ? 'higher' : 'lower'}`
                                  : 'Suggested match — confirm it'
                              : `In register as ${statusLabel(c.match.status, 'variation').toLowerCase()} — not approved`}
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="vchk bad" aria-hidden="true">!</span> Not found in the register
                        <div className="vsub bad-t">Same building, amount within 2 % — nothing matches</div>
                      </>
                    )}
                    {canEdit ? (
                      <div className="var-act">
                        {c.match && !c.row.match_confirmed && APPROVED.has(c.match.status) ? (
                          <Button kind="secondary" className="btn-sm" onClick={() => void upd(c, { variation_item_id: c.match!.id, match_confirmed: true }, `Confirmed ${c.row.ref_on_claim ?? ''} = ${c.match!.number ?? ''}`)}>
                            Confirm match
                          </Button>
                        ) : null}
                        {pickFor === c.row.id ? (
                          <select
                            autoFocus
                            aria-label="Pick the variation"
                            defaultValue=""
                            onBlur={() => setPickFor(null)}
                            onChange={(e) => {
                              setPickFor(null);
                              if (e.target.value) void upd(c, { variation_item_id: e.target.value, match_confirmed: true }, `Linked ${c.row.ref_on_claim ?? ''} to a register variation`);
                            }}
                          >
                            <option value="">Choose…</option>
                            {reg.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.number} · {r.title.slice(0, 50)} · {money(r.amount)} · {statusLabel(r.status, 'variation')}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <button type="button" className="linkbtn small" onClick={() => setPickFor(c.row.id)}>
                            {c.match ? 'Pick another' : 'Pick from register'}
                          </button>
                        )}
                        {c.row.match_confirmed ? (
                          <button type="button" className="linkbtn small" onClick={() => void upd(c, { variation_item_id: null, match_confirmed: false }, `Unlinked ${c.row.ref_on_claim ?? ''}`)}>
                            Undo match
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="linkbtn danger small"
                          onClick={() => window.confirm(`Remove ${c.row.ref_on_claim ?? 'this variation'} from the claim?`) && void write('claim_variations', 'delete', { id: c.row.id, summary: 'Removed a variation from the claim' }).then(onChanged)}
                        >
                          Remove
                        </button>
                      </div>
                    ) : null}
                  </td>
                  <td className="right">
                    <span className={c.swi < (c.row.claimed_now ?? 0) ? 'was' : 'strong'}>{money(c.row.claimed_now)}</span>
                    <div className={`vsub ${c.swi < (c.row.claimed_now ?? 0) ? 'bad-t' : ''}`}>
                      {c.swi < (c.row.claimed_now ?? 0) ? (c.swi ? `SWI accepts ${money(c.swi)}` : 'Not counted') : c.match?.amount ? `${money(((c.row.claimed_now ?? 0) / c.match.amount) * 100)} % of it` : ''}
                    </div>
                    {canEdit ? (
                      <input
                        className="cell r"
                        style={{ width: 96, marginTop: 4 }}
                        aria-label={`SWI accepts for ${c.row.ref_on_claim ?? ''}`}
                        placeholder="SWI amount"
                        defaultValue={c.row.swi_amount ?? ''}
                        onBlur={(e) => {
                          const v = e.target.value === '' ? null : Number(e.target.value);
                          if (v !== c.row.swi_amount) void upd(c, { swi_amount: v }, `SWI amount for ${c.row.ref_on_claim ?? ''} → ${e.target.value || 'by the check'}`);
                        }}
                      />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted small">No variations listed on this claim.</p>
      )}
      {canEdit ? (
        adding ? (
          <div className="fu-form">
            <input placeholder="Ref on claim (VO-17)" value={f.ref} onChange={(e) => setF({ ...f, ref: e.target.value })} aria-label="Reference on the claim" />
            <input placeholder="Description" value={f.desc} onChange={(e) => setF({ ...f, desc: e.target.value })} aria-label="Description" />
            <input placeholder="Listed approved value" inputMode="decimal" value={f.listed} onChange={(e) => setF({ ...f, listed: e.target.value })} aria-label="Approved value listed" />
            <input placeholder="Claimed this period" inputMode="decimal" value={f.now} onChange={(e) => setF({ ...f, now: e.target.value })} aria-label="Claimed this period" />
            <Button kind="primary" onClick={() => void add()} disabled={!f.ref && !f.desc}>
              Add
            </Button>
            <Button onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        ) : (
          <Button kind="ghost" onClick={() => setAdding(true)}>
            + Variation on this claim
          </Button>
        )
      ) : null}
      <p className="muted small" style={{ marginTop: 8 }}>
        Check 1 is what PBS lists on the claim. Check 2 matches each to the variations register by building and amount — amounts within 2 % are suggested for a PM to confirm. Anything not approved in PM C3 stays listed, flagged, and out of SWI’s figure.
      </p>
    </div>
  );
}
